import { AlertCircle, Check, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/common/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/common/ui/card";

import { acceptInvitation } from "./api/organization-api";

export function AcceptInvitationPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const handleAccept = async (): Promise<void> => {
    if (!token) return;

    setIsAccepting(true);
    setError(false);

    try {
      await acceptInvitation(token);
      setAccepted(true);
      redirectTimerRef.current = setTimeout(() => {
        navigate("/organization");
      }, 1500);
    } catch {
      setError(true);
    } finally {
      setIsAccepting(false);
    }
  };

  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pb-8 pt-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10">
              <Check className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-semibold">Welcome!</h2>
            <p className="mt-2 text-muted-foreground">
              You have successfully joined the organization.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">Redirecting to organization...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pb-8 pt-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold">Invitation Invalid</h2>
            <p className="mt-2 text-muted-foreground">
              This invitation link has expired or is no longer valid.
            </p>
            <Button className="mt-6" variant="outline" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Users className="h-7 w-7 text-primary" />
          </div>
          <CardTitle className="text-xl">You have been invited</CardTitle>
        </CardHeader>

        <CardFooter className="flex flex-col gap-3">
          <Button className="w-full" size="lg" onClick={handleAccept} disabled={isAccepting}>
            {isAccepting ? (
              "Joining..."
            ) : (
              <>
                <Users className="mr-2 h-4 w-4" />
                Accept & Join
              </>
            )}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            By accepting, you agree to the organization&apos;s policies.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
